import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { City } from '@shared-features/models';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { FilterService } from '../../../filter/services/filter.service';
import { ConfigConstant } from '@shared/constants';
import { BusinessType, DateRangeType, FormAction } from '@shared-features/enums/shared-enums.enum';
import { TabGroup } from '@shared-features/enums/tab.enum';
import { FunctionUtil } from '@shared/utils';
import { CityAPI } from '@shared-features/services';

@Component({
  selector: 'app-delivery-notes-filter',
  templateUrl: './delivery-notes-filter.component.html',
  styleUrls: ['./delivery-notes-filter.component.scss'],
})
export class DeliveryNotesFilterComponent extends BaseComponent implements OnInit {
  @Input() action: Observable<FormAction>;

  form: FormGroup;
  params: any = null;
  cities: City[] = [];

  // enums
  SharedConstants = ConfigConstant;
  DateRangeType = DateRangeType;

  constructor(
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private cityAPI: CityAPI
  ) {
    super();
  }

  ngOnInit(): void {
    this.getCities();
    this.subscribeToQueryParams();
    this.subscribeToFilterAction();
    this.initForm();
  }

  getCities() {
    this.load(this.cityAPI.getActiveCities(BusinessType.B2B), {
      isLoadingTransparent: true,
    }).subscribe(
      (cities) => {
        this.cities = cities;
        this.initForm();
      },
      (error) => {
        this.asyncFeedbackService.showFailureMessage(
          error ? error.errors[0] : this.translate('ErrorMessages.ErrorHappened')
        );
      }
    );
  }

  subscribeToQueryParams(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params && Object.keys(params).length !== 0) {
        this.params = params;
      }
    });
  }

  subscribeToFilterAction() {
    this.action.pipe(takeUntil(this.destroy$)).subscribe((action: FormAction) => {
      switch (action) {
        case FormAction.Reset:
          this.resetForm();
          break;

        case FormAction.Cancel:
          this.cancelChanges();
          break;

        case FormAction.Submit:
          this.submitForm();
          break;
      }
    });
  }

  getFilterValuesBasedOnURLParams() {
    return {
      dateRange:
        this.params.dateRange === DateRangeType.ALL_TIME
          ? DateRangeType.ALL_TIME
          : String(this.params.dateRange),
      startFrom: this.params.startFrom ? String(this.params.startFrom) : null,
      endAt: this.params.endAt ? String(this.params.endAt) : null,
      from: this.params.from ? Number(this.params.from) : null,
      to: this.params.to ? Number(this.params.to) : null,
      waitingCharge: this.params.waitingCharge ? String(this.params.waitingCharge) : null,
      multiDis: this.params.multiDis ? String(this.params.multiDis) : null,
    };
  }

  initForm() {
    if (this.params && Object.keys(this.params).length !== 0) {
      const filterValues = this.getFilterValuesBasedOnURLParams();
      this.form = new FormGroup({
        dateRange: new FormControl({
          range: filterValues.dateRange || DateRangeType.TODAY,
          startFrom: filterValues.from || '',
          endAt: filterValues.to || '',
        }),
        from: new FormControl(
          this.cities.filter((item) => filterValues?.from == item.id)[0] || null
        ),
        to: new FormControl(this.cities.filter((item) => filterValues?.to == item.id)[0] || null),
        waitingCharge: new FormControl(filterValues?.waitingCharge),
        multiDis: new FormControl(filterValues?.multiDis),
      });
    } else {
      this.form = new FormGroup({
        dateRange: new FormControl({ range: DateRangeType.ALL_TIME, startFrom: '', endAt: '' }),
        from: new FormControl(null),
        to: new FormControl(null),
        waitingCharge: new FormControl(null),
        multiDis: new FormControl(null),
      });
    }
  }

  cancelChanges() {
    if (this.params && Object.keys(this.params).length !== 0) {
      const filterValues = this.getFilterValuesBasedOnURLParams();
      this.form.setValue({
        dateRange: {
          range: filterValues.dateRange || DateRangeType.TODAY,
          startFrom: filterValues.from || '',
          endAt: filterValues.to || '',
        },
        from: filterValues.from,
        to: filterValues.to,
        waitingCharge: filterValues.waitingCharge,
        multiDis: filterValues.multiDis,
      });
    } else {
      this.resetForm();
    }
  }

  resetForm() {
    this.form.setValue({
      dateRange: { range: DateRangeType.ALL_TIME, startFrom: '', endAt: '' },
      from: null,
      to: null,
      waitingCharge: null,
      multiDis: null,
    });

    this.submitForm();
  }

  submitForm() {
    const submittedFilterValues = this.form.value;

    if (this.form.controls.dateRange.value.startFrom && this.form.controls.dateRange.value.endAt) {
      submittedFilterValues['dateRange'].startFrom = FunctionUtil.convertDate(
        this.form.controls.dateRange.value.startFrom
      );
      submittedFilterValues['dateRange'].endAt = FunctionUtil.convertDate(
        this.form.controls.dateRange.value.endAt
      );
    }
    submittedFilterValues.tabGroup = TabGroup.DeliveryNotes;
    submittedFilterValues.startFrom = submittedFilterValues.dateRange?.startFrom;
    submittedFilterValues.endAt = submittedFilterValues.dateRange?.endAt;
    submittedFilterValues.dateRange = submittedFilterValues.dateRange?.range;
    submittedFilterValues.from = submittedFilterValues.from?.id;
    submittedFilterValues.to = submittedFilterValues.to?.id;
    this.filterService.navigateToTargetPageWithFilter(submittedFilterValues);
  }
}
